import { Button, Flex, Form } from 'antd';
import { useCallback, useState } from 'react';

import { Left } from '../../../../Common/Icon';
import { Question } from '../../../../../store/subCategory';

import styles from './index.module.scss';
import SurveyStep from './SurveyStep';
import { TipType } from '../../../../../store/statistic';

interface ModalSurveyProps {
  isOpen: boolean;

  questions: Question[];
  onFinish: (formValues: FormValues) => void;
  onExit?: () => void;
}

export interface FormValues {
  [key: string]: {
    value: string | null | number | boolean | string[];
    tipType?: TipType | null;
  };
}

function ModalSurvey({ questions, onFinish, onExit }: ModalSurveyProps) {
  const [form] = Form.useForm<FormValues>();
  const [current, setCurrent] = useState(0);
  const [validSteps, setValidSteps] = useState<boolean[]>([]);

  const validateFormValues = useCallback(() => {
    const values = form.getFieldsValue();

    const newValidSteps = questions.map((q) => !!values[q.id]?.toString());

    setValidSteps(newValidSteps);

    return newValidSteps;
  }, [questions]);

  const next = () => {
    const newValidSteps = validateFormValues();

    if (newValidSteps[current]) {
      setCurrent((prevState) => prevState + 1);
    }
  };
  const prev = () => setCurrent((prevState) => prevState - 1);

  const submit = (values: FormValues) => {
    const newValidSteps = validateFormValues();

    if (newValidSteps.every(Boolean)) {
      const result = questions.reduce((acc, question) => {
        const answer = values[question.id];

        if (answer !== undefined) {
          const tipType =
            question.type === 'scale'
              ? form.getFieldValue(`${question.id}-type`)
              : null;

          acc[question.id] = {
            value: answer,
            tipType,
          };
        }

        return acc;
      }, {} as Record<string, any>);

      onFinish(result);
    }
  };

  return (
    <Form<FormValues>
      form={form}
      className={styles.wrapper}
      onFinish={submit}
      onValuesChange={() => setValidSteps(validateFormValues())}
    >
      <div
        className={styles.container}
        style={{ marginLeft: `calc(${-current} * 100%)` }}
      >
        {questions.map((question) => (
          <SurveyStep
            key={question.id}
            form={form}
            stepCount={questions.length}
            stepCurrent={current}
            question={question}
          />
        ))}
      </div>
      <Flex
        justify={
          current === 0 && onExit
            ? 'space-between'
            : current === 0
            ? 'flex-end'
            : 'space-between'
        }
        className={styles.footer}
      >
        {current === 0 && onExit && (
          <Button type="text" className={styles.control} onClick={onExit}>
            <Left /> Previous
          </Button>
        )}
        {current > 0 && (
          <Button type="text" className={styles.control} onClick={prev}>
            <Left /> Previous
          </Button>
        )}
        {current < questions.length - 1 && (
          <Button
            type="text"
            className={styles.control}
            onClick={next}
            disabled={!validSteps[current]}
          >
            Next <Left style={{ transform: 'rotate(180deg)' }} />
          </Button>
        )}
        {current === questions.length - 1 && (
          <Button
            type="primary"
            onClick={form.submit}
            style={{ height: 50 }}
            className={styles.submit}
            disabled={!validSteps[current]}
          >
            Submit
          </Button>
        )}
      </Flex>
    </Form>
  );
}

export default ModalSurvey;
